import { useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Hammer, CheckCircle, Download, Loader2 } from 'lucide-react'
import type { TestBuildInput, TestBuildResult } from '../../types/artefact-templates'
import { TARGET_OS_OPTIONS } from '../../types/velociraptor'
import { useTemplateTestBuild } from '../../hooks/use-artefact-templates'
import { tokenStorage } from '../../services/api'
import { API_BASE_URL } from '../../services/api-base-url'

const BYTES_PER_KIB = 1024
const BYTES_PER_MIB = BYTES_PER_KIB * BYTES_PER_KIB
const HASH_DISPLAY_LENGTH = 16

function formatBytes(bytes: number): string {
  if (bytes < BYTES_PER_KIB) return `${String(bytes)} B`
  if (bytes < BYTES_PER_MIB) return `${(bytes / BYTES_PER_KIB).toFixed(1)} KB`
  return `${(bytes / BYTES_PER_MIB).toFixed(1)} MB`
}

function triggerFileDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.append(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

function extractFilename(disposition: string | null): string {
  if (!disposition) return 'collector'
  const match = disposition.match(/filename="?([^"]+)"?/)
  return match?.[1] ?? 'collector'
}

const SELECT_CLASS =
  'w-full text-sm px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-[#4e5e82] focus:border-transparent disabled:opacity-50'
const INPUT_CLASS =
  'w-full text-sm px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-[#4e5e82] focus:border-transparent disabled:opacity-50'

interface TestBuildDialogProps {
  isOpen: boolean
  templateId: string
  templateName: string
  onClose: () => void
  onBuildComplete: () => void
}

function useTestBuildDownload(flowId: string | undefined): {
  downloading: boolean
  downloadError: string | null
  handleDownload: () => Promise<void>
  clearError: () => void
} {
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  const handleDownload = async (): Promise<void> => {
    if (!flowId) return
    setDownloading(true)
    setDownloadError(null)
    try {
      const token = tokenStorage.getAccessToken()
      const response = await fetch(
        `${API_BASE_URL}/org/artefact-templates/test-builds/${flowId}/download`,
        {
          headers: { Authorization: `Bearer ${token ?? ''}` },
        }
      )
      if (!response.ok) {
        const body = await response.text()
        throw new Error(body || 'Failed to download collector')
      }
      const filename = extractFilename(response.headers.get('Content-Disposition'))
      triggerFileDownload(await response.blob(), filename)
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Download failed')
    } finally {
      setDownloading(false)
    }
  }

  return {
    downloading,
    downloadError,
    handleDownload,
    clearError: () => {
      setDownloadError(null)
    },
  }
}

function TestBuildResultPanel({
  result,
  onDownload,
  downloading,
  downloadError,
}: {
  result: TestBuildResult
  onDownload: () => void
  downloading: boolean
  downloadError: string | null
}): ReactElement {
  if (!result.success) {
    return (
      <div
        className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/50 dark:text-red-300"
        role="alert"
      >
        {result.errorMessage ?? 'Build failed'}
      </div>
    )
  }
  return (
    <div className="mb-4 rounded-lg bg-green-50 p-4 dark:bg-green-950/50" role="status">
      <div className="mb-2 flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-300">
        <CheckCircle className="h-4 w-4 shrink-0" />
        Collector built successfully
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-green-700 dark:text-green-300">
        <dt className="font-medium">Platform</dt>
        <dd>{result.targetOs}</dd>
        <dt className="font-medium">Artefacts</dt>
        <dd>{result.artefactsIncluded.length} included</dd>
        {result.collectorSizeBytes !== null && (
          <>
            <dt className="font-medium">Size</dt>
            <dd>{formatBytes(result.collectorSizeBytes)}</dd>
          </>
        )}
        {result.collectorHash && (
          <>
            <dt className="font-medium">SHA-256</dt>
            <dd className="truncate font-mono" title={result.collectorHash}>
              {result.collectorHash.slice(0, HASH_DISPLAY_LENGTH)}...
            </dd>
          </>
        )}
      </dl>
      {result.flowId && (
        <button
          onClick={onDownload}
          disabled={downloading}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
        >
          {downloading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Downloading...
            </>
          ) : (
            <>
              <Download className="h-4 w-4" />
              Download Collector
            </>
          )}
        </button>
      )}
      {downloadError && (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400">{downloadError}</p>
      )}
    </div>
  )
}

function TestBuildForm({
  targetOs,
  encryptionPassword,
  building,
  onOsChange,
  onPasswordChange,
}: {
  targetOs: string
  encryptionPassword: string
  building: boolean
  onOsChange: (v: string) => void
  onPasswordChange: (v: string) => void
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="space-y-4 px-6 py-4">
      <p className="text-sm text-slate-600 dark:text-slate-300">
        {t('velociraptor.orgTemplates.testBuildDescription')}
      </p>
      <div>
        <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
          {t('velociraptor.orgTemplates.testBuildTargetOs')}
        </label>
        <select
          value={targetOs}
          onChange={(ev) => {
            onOsChange(ev.target.value)
          }}
          disabled={building}
          className={SELECT_CLASS}
        >
          {TARGET_OS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
          {t('velociraptor.orgTemplates.testBuildEncryption')}
        </label>
        <input
          type="password"
          value={encryptionPassword}
          onChange={(ev) => {
            onPasswordChange(ev.target.value)
          }}
          disabled={building}
          placeholder={t('velociraptor.orgTemplates.testBuildEncryptionPlaceholder')}
          className={INPUT_CLASS}
        />
      </div>
    </div>
  )
}

interface UseTestBuildDialogResult {
  targetOs: string
  encryptionPassword: string
  result: TestBuildResult | null
  building: boolean
  error: string | null
  downloading: boolean
  downloadError: string | null
  setTargetOs: (v: string) => void
  setEncryptionPassword: (v: string) => void
  handleBuild: () => Promise<void>
  handleClose: () => void
  handleDownload: () => Promise<void>
}

function useTestBuildDialog({
  templateId,
  onClose,
  onBuildComplete,
}: {
  templateId: string
  onClose: () => void
  onBuildComplete: () => void
}): UseTestBuildDialogResult {
  const { building, error, triggerBuild } = useTemplateTestBuild()
  const [targetOs, setTargetOs] = useState('windows_x86')
  const [encryptionPassword, setEncryptionPassword] = useState('')
  const [result, setResult] = useState<TestBuildResult | null>(null)
  const { downloading, downloadError, handleDownload } = useTestBuildDownload(
    result?.flowId ?? undefined
  )

  const handleBuild = async (): Promise<void> => {
    setResult(null)
    const input: TestBuildInput = { targetOs }
    if (encryptionPassword) input.encryptionPassword = encryptionPassword
    const buildResult = await triggerBuild(templateId, input)
    if (buildResult) setResult(buildResult)
  }

  const handleClose = (): void => {
    if (result?.success) onBuildComplete()
    setResult(null)
    setEncryptionPassword('')
    onClose()
  }

  return {
    targetOs,
    encryptionPassword,
    result,
    building,
    error,
    downloading,
    downloadError,
    setTargetOs,
    setEncryptionPassword,
    handleBuild,
    handleClose,
    handleDownload,
  }
}

interface TestBuildDialogCardProps extends UseTestBuildDialogResult {
  templateName: string
}

function TestBuildDialogCard({
  templateName,
  targetOs,
  encryptionPassword,
  result,
  building,
  error,
  downloading,
  downloadError,
  setTargetOs,
  setEncryptionPassword,
  handleBuild,
  handleClose,
  handleDownload,
}: TestBuildDialogCardProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="w-full max-w-md rounded-2xl bg-white shadow-xl dark:bg-slate-800">
      <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-700">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-800 dark:text-slate-100">
            {t('velociraptor.orgTemplates.testBuildTitle')}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">{templateName}</p>
        </div>
        <button
          onClick={handleClose}
          className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
          aria-label={t('velociraptor.configModal.close')}
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <TestBuildForm
        targetOs={targetOs}
        encryptionPassword={encryptionPassword}
        building={building}
        onOsChange={setTargetOs}
        onPasswordChange={setEncryptionPassword}
      />
      <div className="px-6">
        {result && (
          <TestBuildResultPanel
            result={result}
            onDownload={() => { void handleDownload() }}
            downloading={downloading}
            downloadError={downloadError}
          />
        )}
        {error && !result && (
          <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/50 dark:text-red-300" role="alert">
            {error}
          </div>
        )}
      </div>
      <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4 dark:border-slate-700">
        <button onClick={handleClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700">
          {t('collections.artefactTemplates.cancel')}
        </button>
        <button
          onClick={() => { void handleBuild() }}
          disabled={building}
          className="flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white hover:bg-[#5c6e94] focus:ring-2 focus:ring-[#4e5e82] focus:outline-none disabled:opacity-50"
        >
          {building ? (
            <><Loader2 className="h-4 w-4 animate-spin" />{t('velociraptor.orgTemplates.testBuildBuilding')}</>
          ) : (
            <><Hammer className="h-4 w-4" />{t('velociraptor.orgTemplates.testBuildStart')}</>
          )}
        </button>
      </div>
    </div>
  )
}

export function TestBuildDialog({
  isOpen,
  templateId,
  templateName,
  onClose,
  onBuildComplete,
}: TestBuildDialogProps): ReactElement | null {
  const state = useTestBuildDialog({ templateId, onClose, onBuildComplete })
  if (!isOpen) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <TestBuildDialogCard templateName={templateName} {...state} />
    </div>
  )
}
